class CategoryTable extends HTMLElement {
  connectedCallback() {
    this.source = this.querySelector('.category-table__source');
    this.categoryList = this.querySelector('.category-table__categories');
    this.leftSlot = this.querySelector('.category-table__left-slot');
    this.rightSlot = this.querySelector('.category-table__right-slot, .category-table__right');
    this.featureSlot = this.querySelector('.category-table__feature-slot');
    this.units = Array.from(this.querySelectorAll('.category-table__unit'));

    this.categories = this.source
      ? Array.from(this.source.querySelectorAll('[data-category-table-category]'))
      : [];

    if (this.categories.length === 0) return;

    this.relocateCategoryList();
    this.buildRadios();
    this.unitLabels = Array.from(this.querySelectorAll('[data-unit-label]'));
    this.unitValues = Array.from(this.querySelectorAll('[data-unit-value]'));

    const defaultKey = this.dataset.defaultCategory;
    const initial = this.categories.find((cat) => cat.dataset.key === defaultKey) || this.categories[0];
    this.activateCategory(initial.dataset.key);

    this.units.forEach((btn) => {
      btn.addEventListener('click', this.handleUnitClick);
      btn.addEventListener('keydown', this.handleUnitKeyDown);
    });
  }

  relocateCategoryList() {
    // When this category-table lives inside a `_tab-panel`, hoist the radio list
    // up next to the eyebrow heading so they sit on a single line — matching
    // the Figma layout. Falls back to in-place rendering otherwise.
    const panelContent = this.closest('.tabbed-content__panel-content');
    if (!panelContent) return;
    const eyebrow = panelContent.querySelector('.tabbed-content__eyebrow');
    if (!eyebrow) return;
    eyebrow.classList.add('tabbed-content__eyebrow--with-controls');
    // Wrap the existing title parts (span + sup) so they stay grouped on the
    // left while the controls float to the right.
    if (!eyebrow.querySelector('.tabbed-content__eyebrow-title')) {
      const titleWrap = document.createElement('span');
      titleWrap.className = 'tabbed-content__eyebrow-title';
      const existing = Array.from(eyebrow.childNodes);
      existing.forEach((node) => titleWrap.appendChild(node));
      eyebrow.appendChild(titleWrap);
    }
    eyebrow.appendChild(this.categoryList);
    // Hide the now-empty header in the block so there's no extra gap.
    const header = this.querySelector('.category-table__header');
    if (header) header.hidden = true;
  }

  buildRadios() {
    this.radioButtons = this.categories.map((cat) => {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'category-table__category';
      btn.setAttribute('role', 'radio');
      btn.setAttribute('aria-checked', 'false');
      btn.tabIndex = -1;
      btn.dataset.category = cat.dataset.key;
      btn.innerHTML = `
        <span class="category-table__radio" aria-hidden="true"><span class="category-table__radio-dot"></span></span>
        <span class="category-table__category-label">${cat.dataset.label || cat.dataset.key}</span>
      `;
      btn.addEventListener('click', () => this.activateCategory(cat.dataset.key));
      btn.addEventListener('keydown', this.handleCategoryKeyDown);
      this.categoryList.appendChild(btn);
      return btn;
    });
  }

  handleCategoryKeyDown = (event) => {
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
    event.preventDefault();
    const index = this.radioButtons.indexOf(event.currentTarget);
    if (index === -1) return;
    const delta = event.key === 'ArrowRight' ? 1 : -1;
    const next = this.radioButtons[(index + delta + this.radioButtons.length) % this.radioButtons.length];
    this.activateCategory(next.dataset.category);
    next.focus();
  };

  handleUnitClick = (event) => {
    this.activateUnit(event.currentTarget.dataset.unit);
  };

  handleUnitKeyDown = (event) => {
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
    event.preventDefault();
    const index = this.units.indexOf(event.currentTarget);
    if (index === -1) return;
    const delta = event.key === 'ArrowRight' ? 1 : -1;
    const next = this.units[(index + delta + this.units.length) % this.units.length];
    this.activateUnit(next.dataset.unit);
    next.focus();
  };

  activateCategory(key) {
    if (!key) return;
    this.dataset.activeCategory = key;
    this.radioButtons.forEach((btn) => {
      const isActive = btn.dataset.category === key;
      btn.setAttribute('aria-checked', String(isActive));
      btn.tabIndex = isActive ? 0 : -1;
    });

    this.leftSlot.replaceChildren();
    this.rightSlot.replaceChildren();
    if (this.featureSlot) this.featureSlot.replaceChildren();

    const active = this.categories.find((cat) => cat.dataset.key === key);
    if (!active) return;

    Array.from(active.children).forEach((child) => {
      const slot = child.dataset && child.dataset.slot;
      if (slot !== 'left' && slot !== 'right' && slot !== 'feature') return;
      const clone = child.cloneNode(true);
      clone.removeAttribute('hidden');
      const target = slot === 'left' ? this.leftSlot : slot === 'right' ? this.rightSlot : this.featureSlot;
      if (target) target.appendChild(clone);
    });

    this.unitLabels = Array.from(this.querySelectorAll('[data-unit-label]'));
    this.unitValues = Array.from(this.querySelectorAll('[data-unit-value]'));
    this.applyUnit(this.dataset.activeUnit || 'eu');
  }

  activateUnit(unit) {
    if (!unit) return;
    this.dataset.activeUnit = unit;
    this.units.forEach((btn) => {
      const isActive = btn.dataset.unit === unit;
      btn.setAttribute('aria-selected', String(isActive));
      btn.tabIndex = isActive ? 0 : -1;
    });
    this.applyUnit(unit);
  }

  applyUnit(unit) {
    this.unitLabels.forEach((node) => {
      if (node.dataset.unitLabel === unit) node.removeAttribute('hidden');
      else node.setAttribute('hidden', '');
    });
    this.unitValues.forEach((node) => {
      if (node.dataset.unitValue === unit) node.removeAttribute('hidden');
      else node.setAttribute('hidden', '');
    });
  }
}

if (!customElements.get('category-table')) {
  customElements.define('category-table', CategoryTable);
}
